import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { msalInstance } from '../../auth/msalConfig';
import { markSignedOut } from '../../auth/authStatus';
import { useWhoami, whoamiQueryKey } from '../../api/whoami';
import { useToast } from '../../components';

/**
 * The signed-in SiteAdministrator's account control, top-right in the console
 * `SaTopBar` (deliberately unlike the customer shell's sidebar-bottom menu — the
 * console is a distinct surface). Shows the operator identity with a
 * SiteAdministrator badge and an explicit "no customer scope" note, plus a theme
 * toggle and a real sign-out. No language switcher: the console is en-US only
 * (docs/design/fe-build-goals/README.md "Site Admin is en-US only").
 */
export function SaAccountMenu() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { mode, setMode } = useColorScheme();
  const { data: whoami } = useWhoami();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const email = whoami?.authenticated.email ?? '';
  const initial = (email || '?').charAt(0).toUpperCase();
  const isDark = mode === 'dark';

  const closeMenu = () => setAnchorEl(null);

  const handleThemeToggle = () => setMode(isDark ? 'light' : 'dark');

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await apiClient.post('/auth/sign-out');
    } catch {
      // Best-effort server cleanup; proceed to the identity-provider logout regardless.
    }
    queryClient.removeQueries({ queryKey: whoamiQueryKey });
    markSignedOut();
    try {
      await msalInstance.logoutRedirect();
    } catch {
      setSigningOut(false);
      toast.error(t('errors.generic'));
      // See SidebarAccountMenu: without this the now-account-less page sits
      // inside `RequireAuth` and is bounced straight to Microsoft.
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t('siteAdmin:shell.account.open')}
        onClick={(event: MouseEvent<HTMLElement>) => {
          const trigger = event.currentTarget;
          setAnchorEl((prev) => (prev ? null : trigger));
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          pl: 0.5,
          pr: 1,
          py: 0.5,
          borderRadius: 999,
          border: '1px solid',
          borderColor: menuOpen ? 'divider' : 'transparent',
          bgcolor: menuOpen ? 'action.hover' : 'transparent',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {initial}
        </Avatar>
        <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 280, borderRadius: '12px', mt: 1 } } }}
      >
        <Box sx={{ px: 1.25, pt: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {email}
            </Typography>
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              px: 1,
              py: '2px',
              borderRadius: 999,
              bgcolor: 'rgba(0,71,133,0.12)',
              color: 'primary.dark',
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 700,
              fontSize: 10,
              lineHeight: 1.6,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            <ShieldOutlined sx={{ fontSize: 11 }} />
            {t('siteAdmin:shell.account.roleBadge')}
          </Box>
          <Typography sx={{ mt: 0.75, fontSize: 11, color: 'text.secondary' }}>
            {t('siteAdmin:shell.account.scopeNote')}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleThemeToggle}>
          <ListItemIcon>
            {isDark ? (
              <LightModeOutlined fontSize="small" />
            ) : (
              <DarkModeOutlined fontSize="small" />
            )}
          </ListItemIcon>
          {isDark ? t('userMenu.lightMode') : t('userMenu.darkMode')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut} disabled={signingOut} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            {signingOut ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LogoutOutlined fontSize="small" sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          {signingOut ? t('signOut.pending') : t('nav.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
}
